import { create } from "zustand";
import { GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import { PostModel } from "common/models/BaseModel";
import {
  ICirculationGridFilter,
  IDueMaterial,
  IDueMaterialGridFilter,
  IProgramCirculationGridFilter,
  ITitleVolumeGridFilter,
  IVisitorGridFilter,
  IRecentlyAddedBooksGridFilter,
} from "../api/types";
import dayjs from "dayjs";

export interface DashboardStore {
  visitorPostModel: PostModel<IVisitorGridFilter>;
  handleVisitorFiltering: (filterModel: IVisitorGridFilter) => void;
  handleVisitorPagination: (pageModel: GridPaginationModel) => void;
  handleVisitorSorting: (sortModel: GridSortModel) => void;

  titleVolumePostModel: PostModel<ITitleVolumeGridFilter>;
  handleTitleVolumeFiltering: (filterModel: ITitleVolumeGridFilter) => void;
  handleTitleVolumePagination: (pageModel: GridPaginationModel) => void;
  handleTitleVolumeSorting: (sortModel: GridSortModel) => void;

  circulationPostModel: PostModel<ICirculationGridFilter>;
  handleCirculationFiltering: (filterModel: ICirculationGridFilter) => void;
  handleCirculationPagination: (pageModel: GridPaginationModel) => void;
  handleCirculationSorting: (sortModel: GridSortModel) => void;

  programCirculationPostModel: PostModel<IProgramCirculationGridFilter>;
  handleProgramCirculationFiltering: (
    filterModel: IProgramCirculationGridFilter,
  ) => void;
  handleProgramCirculationPagination: (pageModel: GridPaginationModel) => void;
  handleProgramCirculationSorting: (sortModel: GridSortModel) => void;

  dueMaterialPostModel: PostModel<IDueMaterialGridFilter>;
  handleDueMaterialFiltering: (filterModel: IDueMaterialGridFilter) => void;
  handleDueMaterialPagination: (pageModel: GridPaginationModel) => void;
  handleDueMaterialSorting: (sortModel: GridSortModel) => void;

  recentlyAddedBooksPostModel: PostModel<IRecentlyAddedBooksGridFilter>;
  handleRecentlyAddedBooksFiltering: (
    filterModel: IRecentlyAddedBooksGridFilter,
  ) => void;
  handleRecentlyAddedBooksPagination: (
    pageModel: GridPaginationModel,
  ) => void;
  handleRecentlyAddedBooksSorting: (sortModel: GridSortModel) => void;

  value: string;
  setValue: (newValue: string) => void;

  dueMemberData: IDueMaterial[];
  setDueMemberData: (data: IDueMaterial[]) => void;
}

const useDashboardStore = create<DashboardStore>()((set) => ({
  value: "1",
  setValue: (newValue) => set({ value: newValue }),

  visitorPostModel: {
    pagingModel: {
      page: 0,
      pageSize: 100,
    },
    filterModel: {
      memberTypeID: null,
    },
  },
  handleVisitorPagination: (pageModel: GridPaginationModel) =>
    set((state) => ({
      visitorPostModel: {
        ...state.visitorPostModel,
        pagingModel: { ...pageModel },
      },
    })),
  handleVisitorFiltering: (filterModel: IVisitorGridFilter) =>
    set((state) => ({
      visitorPostModel: {
        ...state.visitorPostModel,
        filterModel: { ...filterModel },
      },
    })),
  handleVisitorSorting: (sortModel: GridSortModel) =>
    set((state) => ({
      visitorPostModel: {
        ...state.visitorPostModel,
        sortModel: [...sortModel],
      },
    })),

  titleVolumePostModel: {
    pagingModel: {
      page: 0,
      pageSize: 100,
    },
    filterModel: {
      materialTypeID: null,
    },
  },
  handleTitleVolumePagination: (pageModel: GridPaginationModel) =>
    set((state) => ({
      titleVolumePostModel: {
        ...state.titleVolumePostModel,
        pagingModel: { ...pageModel },
      },
    })),
  handleTitleVolumeFiltering: (filterModel: ITitleVolumeGridFilter) =>
    set((state) => ({
      titleVolumePostModel: {
        ...state.titleVolumePostModel,
        filterModel: { ...filterModel },
      },
    })),
  handleTitleVolumeSorting: (sortModel: GridSortModel) =>
    set((state) => ({
      titleVolumePostModel: {
        ...state.titleVolumePostModel,
        sortModel: [...sortModel],
      },
    })),

  circulationPostModel: {
    pagingModel: {
      page: 0,
      pageSize: 100,
    },
    filterModel: {
      memberTypeID: null,
      materialTypeID: null,
      fromDate: dayjs().subtract(6, "month").toString(),
      toDate: dayjs().toString(),
    },
  },
  handleCirculationPagination: (pageModel: GridPaginationModel) =>
    set((state) => ({
      circulationPostModel: {
        ...state.circulationPostModel,
        pagingModel: { ...pageModel },
      },
    })),
  handleCirculationFiltering: (filterModel: ICirculationGridFilter) =>
    set((state) => ({
      circulationPostModel: {
        ...state.circulationPostModel,
        filterModel: { ...filterModel },
      },
    })),
  handleCirculationSorting: (sortModel: GridSortModel) =>
    set((state) => ({
      circulationPostModel: {
        ...state.circulationPostModel,
        sortModel: [...sortModel],
      },
    })),

  programCirculationPostModel: {
    pagingModel: {
      page: 0,
      pageSize: 100,
    },
    filterModel: {
      memberTypeID: null,
      programID: null,
      fromDate: dayjs().subtract(6, "month").toString(),
      toDate: dayjs().toString(),
    },
  },
  handleProgramCirculationPagination: (pageModel: GridPaginationModel) =>
    set((state) => ({
      programCirculationPostModel: {
        ...state.programCirculationPostModel,
        pagingModel: { ...pageModel },
      },
    })),
  handleProgramCirculationFiltering: (
    filterModel: IProgramCirculationGridFilter,
  ) =>
    set((state) => ({
      programCirculationPostModel: {
        ...state.programCirculationPostModel,
        filterModel: { ...filterModel },
      },
    })),
  handleProgramCirculationSorting: (sortModel: GridSortModel) =>
    set((state) => ({
      programCirculationPostModel: {
        ...state.programCirculationPostModel,
        sortModel: [...sortModel],
      },
    })),

  dueMaterialPostModel: {
    pagingModel: {
      page: 0,
      pageSize: 100,
    },
    filterModel: {
      materialAccessionNoID: null,
      accessionNoValue: null,
      memberID: null,
      memberCodeValue: null,
      dueDays: null,
      memberTypeID: null,
      materialTypeID: null,
      dueStatus: null,
      remainingDays: null,
      fineAmount: null,
      fromDueDate: null,
      toDueDate: null,
      fromIssueDate: null,
      toIssueDate: null,
    },
  },
  handleDueMaterialPagination: (pageModel: GridPaginationModel) =>
    set((state) => ({
      dueMaterialPostModel: {
        ...state.dueMaterialPostModel,
        pagingModel: { ...pageModel },
      },
    })),
  handleDueMaterialFiltering: (filterModel: IDueMaterialGridFilter) =>
    set((state) => ({
      dueMaterialPostModel: {
        ...state.dueMaterialPostModel,
        filterModel: { ...filterModel },
      },
    })),
  handleDueMaterialSorting: (sortModel: GridSortModel) =>
    set((state) => ({
      dueMaterialPostModel: {
        ...state.dueMaterialPostModel,
        sortModel: [...sortModel],
      },
    })),
  dueMemberData: [],
  setDueMemberData: (data: IDueMaterial[]) =>
    set((state) => ({
      dueMemberData: [...data],
    })),

  recentlyAddedBooksPostModel: {
    pagingModel: {
      page: 0,
      pageSize: 50,
    },
    filterModel: {
      materialTypeID: null,
      instituteID: null,
      departmentID: null,
      languageID: null,
      fromDate: null,
      toDate: null,
      searchText: null,
    },
    sortModel: [],
  },
  handleRecentlyAddedBooksPagination: (pageModel: GridPaginationModel) =>
    set((state) => ({
      recentlyAddedBooksPostModel: {
        ...state.recentlyAddedBooksPostModel,
        pagingModel: { ...pageModel },
      },
    })),
  handleRecentlyAddedBooksFiltering: (
    filterModel: IRecentlyAddedBooksGridFilter,
  ) =>
    set((state) => ({
      recentlyAddedBooksPostModel: {
        ...state.recentlyAddedBooksPostModel,
        pagingModel: {
          ...state.recentlyAddedBooksPostModel.pagingModel,
          page: 0,
        },
        filterModel: { ...filterModel },
      },
    })),
  handleRecentlyAddedBooksSorting: (sortModel: GridSortModel) =>
    set((state) => ({
      recentlyAddedBooksPostModel: {
        ...state.recentlyAddedBooksPostModel,
        sortModel: [...sortModel],
      },
    })),
}));

export default useDashboardStore;
